import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import { ChevronDown } from '@outdoor-voices/ui';
import { isMobile } from '@outdoor-voices/ui/dist/utils/dom/isMobile';
import { KEY_CODES, KEY_DOWN } from '@outdoor-voices/ui/dist/config/constants';
import { memo, useEffect, useRef, useState } from 'react';
import cc from 'classcat';
import PropTypes from 'prop-types';
import Storage from '@outdoor-voices/ui/dist/utils/Storage';
import { NAV_HEIGHT } from '../../config/constants';
import Button from '../../components/Button';
import Check from '../../components/icons/Check';
import clickEvent from '../../utils/dom/clickEvent';
import css from './InseamDropdown.module.scss';
import smoothScroll from '../../utils/dom/smoothScroll';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
var heights = ['5\' 3"', '5\' 6"', '5\' 9"'];
var storage = new Storage();
var LI = /*#__PURE__*/memo(function (_ref) {
  var product = _ref.product,
      height = _ref.height,
      productId = _ref.productId;
  var subtitle = product.subtitles[height];
  var current = product.id === productId;
  var linkClass = cc([css.link, _defineProperty({}, css.linkFaded, !subtitle.recommended), _defineProperty({}, css.linkOn, current)]);
  return /*#__PURE__*/_jsx("li", {
    className: css.item,
    children: /*#__PURE__*/_jsxs("a", {
      className: linkClass,
      href: product.url,
      children: [/*#__PURE__*/_jsx("h4", {
        className: css.linkTitle,
        children: product.title
      }), /*#__PURE__*/_jsx("div", {
        className: css.linkSubtitle,
        children: subtitle.title
      }), current && /*#__PURE__*/_jsx(Check, {
        className: css.linkIcon
      })]
    })
  }, product.id);
});
LI.propTypes = {
  product: PropTypes.shape({
    id: PropTypes.number,
    title: PropTypes.string,
    subtitles: PropTypes.shape({
      recommended: PropTypes.bool,
      title: PropTypes.string
    }),
    url: PropTypes.string
  }).isRequired,
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  productId: PropTypes.number.isRequired
};

var InseamDropdown = function InseamDropdown(_ref4) {
  var productId = _ref4.productId;

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      open = _useState2[0],
      setOpen = _useState2[1];

  var _useState3 = useState(storage.get('inseam-height') || 'default'),
      _useState4 = _slicedToArray(_useState3, 2),
      currentHeight = _useState4[0],
      setCurrentHeight = _useState4[1];

  var rootRef = useRef(null);
  var products = [{
    id: 6617683558478,
    title: '26" Inseam',
    url: '/products/w-zephyr-pant',
    subtitles: {
      default: {
        title: '26 Inch Inseam',
        recommended: true
      },
      '5\' 3"': {
        title: 'Hits at the ankle',
        recommended: true
      },
      '5\' 6"': {
        title: 'Hits above the ankle',
        recommended: true
      },
      '5\' 9"': {
        title: 'Hits mid-calf',
        recommended: true
      }
    }
  }, {
    id: 6848243990606,
    title: '29" Inseam',
    url: '/products/w-zephyr-longer-inseam',
    subtitles: {
      default: {
        title: '29 Inch Inseam',
        recommended: true
      },
      '5\' 3"': {
        title: 'May gather at the ankle',
        recommended: true
      },
      '5\' 6"': {
        title: 'Hits at the ankle',
        recommended: true
      },
      '5\' 9"': {
        title: 'Hits above the ankle',
        recommended: true
      }
    }
  }];
  useEffect(function () {
    var onClick = function onClick(event) {
      var root = rootRef.current;

      if (!root || root.contains(event.target)) {
        return;
      }

      event.preventDefault();
      setOpen(false);
    };

    var close = function close() {
      if (!rootRef.current) {
        return;
      }

      setOpen(false);
    };

    var onKeyDown = function onKeyDown(_ref5) {
      var key = _ref5.key;

      if (key === KEY_CODES.ESC) {
        close();
      }
    };

    if (open) {
      document.addEventListener(KEY_DOWN, onKeyDown);
      document.addEventListener(clickEvent(), onClick, false);

      if (!isMobile()) {
        return;
      }

      var rect = rootRef.current.getBoundingClientRect();
      smoothScroll(rect.top + window.scrollY - NAV_HEIGHT);
    } else {
      document.removeEventListener(KEY_DOWN, onKeyDown);
      document.removeEventListener(clickEvent(), onClick);
    }

    return function () {
      document.removeEventListener(KEY_DOWN, onKeyDown);
      document.removeEventListener(clickEvent(), onClick);
    };
  }, [open]);

  var setHeight = function setHeight(newHeight) {
    storage.set('inseam-height', newHeight);
    setCurrentHeight(newHeight);
  };

  var product = products.find(function (prod) {
    return prod.id === productId;
  });
  return /*#__PURE__*/_jsxs("div", {
    className: css.root,
    ref: rootRef,
    children: [/*#__PURE__*/_jsx("span", {
      className: css.label,
      children: "Length:"
    }), /*#__PURE__*/_jsx("div", {
      className: cc([css.select, 'select']),
      children: /*#__PURE__*/_jsx(Button, {
        bordered: true,
        className: css.button,
        full: true,
        iconRight: /*#__PURE__*/_jsx(ChevronDown, {}),
        onClick: function onClick() {
          setOpen(true);
        },
        children: product.title
      })
    }), open && /*#__PURE__*/_jsxs("div", {
      className: css.dropdown,
      children: [/*#__PURE__*/_jsxs("header", {
        className: css.header,
        children: [/*#__PURE__*/_jsx("h4", {
          className: css.title,
          children: "See how it fits different heights:"
        }), /*#__PURE__*/_jsx("ul", {
          "aria-label": "Height selection tabs to determine where this product lands based on a height",
          className: css.tabs,
          role: "tablist",
          children: heights.map(function (height, idx) {
            return /*#__PURE__*/_jsx("li", {
              className: css.tabItem,
              children: /*#__PURE__*/_jsx("button", {
                "aria-controls": "panel-".concat(idx),
                "aria-label": "Click to See how it fits for ".concat(height),
                "aria-selected": height === currentHeight,
                className: cc([css.tabButton, 'button', {
                  'button--secondary': height !== currentHeight
                }]),
                id: "tab-".concat(idx),
                onClick: function onClick() {
                  if (height === currentHeight) {
                    return;
                  }

                  setHeight(height);
                },
                role: "tab",
                type: "button",
                children: height
              })
            }, height);
          })
        })]
      }), heights.map(function (height, idx) {
        return /*#__PURE__*/_jsx("ul", {
          "aria-labelledby": "tab-".concat(idx),
          className: css.list,
          hidden: height !== currentHeight,
          id: "panel-".concat(idx),
          role: "tabpanel",
          tabIndex: "0",
          children: products.map(function (prod) {
            return /*#__PURE__*/_jsx(LI, {
              height: height,
              product: prod,
              productId: productId
            }, "".concat(idx, "-").concat(prod.id));
          })
        }, "panel-".concat(idx));
      })]
    })]
  });
};

InseamDropdown.propTypes = {
  productId: PropTypes.number.isRequired
};
export default /*#__PURE__*/memo(InseamDropdown);